import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CatalogListHeader from '../../../components/CatalogListHeader/CatalogListHeader';
import { ActionIcon, Grid, Tooltip } from '@mantine/core';
import { useHistory, useLocation } from 'react-router-dom';
import CatalogContainer from '../../../components/CatalogContainer/CatalogContainer';
import moment from 'moment';
import useDownloadExcel from '../../../hooks/useDownloadExcel/useDownloadExcel';
import { DatePickerInput, DateValue } from '@mantine/dates';
import { AreaChart } from '@mantine/charts';
import { useMemo, useState } from 'react';
import {
  useGetOrdersCleanedReportData,
  useGetOrdersCleanedReportGraphData
} from '../../../hooks/reports/queries';
import momentTz from 'moment-timezone';
import { exportCleanedOrdersReport } from '../../../services/reports.service';
import { IconFileSpreadsheet } from '@tabler/icons-react';
import TableView from './TableView';
import { IOrder } from '../../../interfaces/Order';
import { MRT_PaginationState } from 'mantine-react-table';
import { getRangeDate } from '../datePickerHelper';

const OrdersCleanedReport = () => {
  const { t } = useTranslation([
    'ReportsPage',
    'FieldName',
    'CommonButtons',
    'Notifications',
    'ReviewsPage'
  ]);
  const location = useLocation();
  const { downloadExcelV2, loadingExport } = useDownloadExcel();
  const [dateRangePicker, setDateRange] = useState<[DateValue, DateValue]>([
    moment().startOf('month').toDate(),
    moment().endOf('month').toDate()
  ]);
  const { fromDate, toDate } = getRangeDate(dateRangePicker);

  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const limitPerReq = 9;
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 9
  });
  const {
    data,
    isLoading: isGetLoadingOrders,
    isError
  } = useGetOrdersCleanedReportData(searchParamStoreId, {
    pageNum: pagination.pageIndex,
    limit: limitPerReq,
    fromDate,
    toDate,
    timezone: momentTz.tz.guess(true)
  });

  const { data: graphData } = useGetOrdersCleanedReportGraphData(searchParamStoreId, {
    fromDate,
    toDate,
    timezone: momentTz.tz.guess(true)
  });

  const orders = useMemo(() => {
    return data?.pages?.reduce((list, { orders }: { orders: IOrder[] }) => {
      list.push(...orders);
      return list;
    }, [] as IOrder[]);
  }, [data]);
  const totalRowsCount = useMemo(() => data?.pages[0]?.totalRows || -1, [data]);
  const pages = useMemo(() => Math.ceil(totalRowsCount / 9) || -1, [totalRowsCount]);

  return (
    <CatalogContainer>
      <CatalogListHeader
        title={t('ReportsPage:ordersCleanedReport.title')}
        showCreateButton={false}
      />
      <Grid>
        <Grid.Col span={{ xs: 12, md: 'auto' }}>
          <DatePickerInput
            clearable={false}
            type="range"
            label={t('FieldName:date')}
            placeholder={t('FieldName:date')}
            value={dateRangePicker}
            onChange={setDateRange}
            allowSingleDateInRange
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'content' }} style={{ alignSelf: 'flex-end' }}>
          <Tooltip label={t('CommonButtons:exportList')}>
            <ActionIcon
              disabled={loadingExport}
              onClick={() => {
                downloadExcelV2(async () => {
                  try {
                    return await exportCleanedOrdersReport(searchParamStoreId, {
                      fromDate,
                      toDate,
                      timezone: momentTz.tz.guess(true)
                    });
                  } catch (e) {
                    throw e;
                  }
                }, `Reporte_${searchParamStoreId}`);
              }}
              variant="filled"
              color="#1D6F42"
              aria-label="Descargar_Listado_CSV">
              <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        </Grid.Col>

        <Grid.Col>
          <AreaChart
            h={400}
            data={graphData?.map(
              (dayData: {
                ordersCount: number;
                sales: number;
                _id: {
                  dayOfMonth: number;
                  month: number;
                  year: number;
                };
              }) => {
                return {
                  cleanedDateTime: `${dayData._id.dayOfMonth}/${dayData._id.month}/${dayData._id.year}`,
                  ordersCount: dayData.ordersCount
                };
              }
            )}
            dataKey="cleanedDateTime"
            series={[{ name: 'ordersCount', color: 'indigo.6', label: t('FieldName:orders') }]}
            curveType="linear"
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <TableView
            orders={orders}
            totalRowsCount={totalRowsCount}
            pages={pages}
            isError={isError}
            isLoading={isGetLoadingOrders}
            pagination={pagination}
            setPagination={setPagination}
          />
        </Grid.Col>
      </Grid>
    </CatalogContainer>
  );
};

export default OrdersCleanedReport;
